import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listSistema, putSistema, smtpTest } from '../api/configuracao';
import { apiError } from '../api/client';
import { Mail, MessageSquare, Plug, Send, Save } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

type Tab = 'smtp' | 'whatsapp' | 'integracoes';

const TABS: { id: Tab; label: string; icon: typeof Mail }[] = [
  { id: 'smtp', label: 'SMTP', icon: Mail },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { id: 'integracoes', label: 'Integrações', icon: Plug }
];

const KEYS_BY_TAB: Record<Tab, string[]> = {
  smtp: ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from', 'smtp_use_tls'],
  whatsapp: ['whatsapp_provider', 'whatsapp_token', 'whatsapp_phone'],
  integracoes: ['api_webhook_url', 'api_webhook_secret']
};

export default function ConfiguracoesPage() {
  const [tab, setTab] = useState<Tab>('smtp');
  const qc = useQueryClient();
  const configsQ = useQuery({ queryKey: ['config-sistema'], queryFn: listSistema });

  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (configsQ.data) {
      const obj: Record<string, string> = {};
      configsQ.data.forEach((c) => {
        obj[c.chave] = c.valor ?? '';
      });
      setValues(obj);
    }
  }, [configsQ.data]);

  async function onSalvar() {
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const keys = KEYS_BY_TAB[tab];
      for (const k of keys) {
        await putSistema(k, values[k] ?? '');
      }
      qc.invalidateQueries({ queryKey: ['config-sistema'] });
      setMsg('Configurações salvas com sucesso.');
    } catch (e) {
      setErr(apiError(e, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  }

  function setVal(k: string, v: string) {
    setValues((old) => ({ ...old, [k]: v }));
  }

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="eyebrow">Operação</div>
        <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">Configurações</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">SMTP, WhatsApp e integrações operacionais do sistema.</p>
      </header>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white/90 p-1 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none">
        <div className="flex gap-1 min-w-max">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => {
                setTab(id);
                setMsg(null);
                setErr(null);
              }}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
                tab === id
                  ? 'border-emerald-500/25 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300'
                  : 'border-transparent text-slate-600 hover:text-slate-950 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-900'
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {configsQ.isLoading && (
        <div className="py-10 grid place-items-center">
          <LoadingSpinner />
        </div>
      )}

      {!configsQ.isLoading && tab === 'smtp' && (
        <SmtpForm values={values} setVal={setVal} />
      )}
      {!configsQ.isLoading && tab === 'whatsapp' && (
        <WhatsappForm values={values} setVal={setVal} />
      )}
      {!configsQ.isLoading && tab === 'integracoes' && (
        <IntegracoesForm values={values} setVal={setVal} />
      )}

      {msg && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
          {msg}
        </div>
      )}
      {err && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
          {err}
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={onSalvar} disabled={saving} className="btn-primary">
          <Save className="h-4 w-4" />
          {saving ? 'Salvando...' : 'Salvar configuracoes'}
        </button>
      </div>
    </div>
  );
}

function SmtpForm({ values, setVal }: { values: Record<string, string>; setVal: (k: string, v: string) => void }) {
  const [destinatario, setDestinatario] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  async function onTestar() {
    if (!destinatario) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await smtpTest(destinatario);
      setTestResult(r.ok ? 'Envio de teste OK.' : `Falhou: ${r.detail || 'sem detalhes'}`);
    } catch (e) {
      setTestResult(`Erro: ${apiError(e, '')}`);
    } finally {
      setTesting(false);
    }
  }

  const Field = (k: string, label: string, type = 'text') => (
    <div>
      <label className="field-label">{label}</label>
      <input
        className="field-input"
        type={type}
        value={values[k] ?? ''}
        onChange={(e) => setVal(k, e.target.value)}
      />
    </div>
  );

  return (
    <section className="card-surface p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Field('smtp_host', 'Servidor SMTP')}
        {Field('smtp_port', 'Porta', 'number')}
        {Field('smtp_user', 'Usuário')}
        {Field('smtp_password', 'Senha', 'password')}
        {Field('smtp_from', 'Remetente (From)')}
        <div>
          <label className="field-label">TLS</label>
          <select
            className="field-input"
            value={values.smtp_use_tls ?? 'true'}
            onChange={(e) => setVal('smtp_use_tls', e.target.value)}
          >
            <option value="true">Ativado</option>
            <option value="false">Desativado</option>
          </select>
        </div>
      </div>
      <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-950 mb-2 dark:text-slate-100">Testar envio</h3>
        <div className="flex gap-2 flex-col sm:flex-row">
          <input
            type="email"
            placeholder="destinatario@exemplo.com"
            value={destinatario}
            onChange={(e) => setDestinatario(e.target.value)}
            className="field-input flex-1"
          />
          <button onClick={onTestar} disabled={testing || !destinatario} className="btn-secondary">
            <Send className="h-4 w-4" />
            {testing ? 'Enviando...' : 'Testar envio'}
          </button>
        </div>
        {testResult && (
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{testResult}</p>
        )}
      </div>
    </section>
  );
}

function WhatsappForm({ values, setVal }: { values: Record<string, string>; setVal: (k: string, v: string) => void }) {
  return (
    <section className="card-surface p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="field-label">Provedor</label>
          <select
            className="field-input"
            value={values.whatsapp_provider ?? ''}
            onChange={(e) => setVal('whatsapp_provider', e.target.value)}
          >
            <option value="">Nenhum</option>
            <option value="meta">Meta Cloud API</option>
            <option value="twilio">Twilio</option>
            <option value="zapi">Z-API</option>
          </select>
        </div>
        <div>
          <label className="field-label">Token</label>
          <input
            type="password"
            className="field-input"
            value={values.whatsapp_token ?? ''}
            onChange={(e) => setVal('whatsapp_token', e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">Número remetente</label>
          <input
            className="field-input"
            value={values.whatsapp_phone ?? ''}
            onChange={(e) => setVal('whatsapp_phone', e.target.value)}
          />
        </div>
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        Integração com WhatsApp sera habilitada em fase posterior.
      </p>
    </section>
  );
}

function IntegracoesForm({ values, setVal }: { values: Record<string, string>; setVal: (k: string, v: string) => void }) {
  return (
    <section className="card-surface p-4 space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="field-label">URL de Webhook</label>
          <input
            className="field-input"
            value={values.api_webhook_url ?? ''}
            onChange={(e) => setVal('api_webhook_url', e.target.value)}
            placeholder="https://exemplo.com/webhook"
          />
        </div>
        <div>
          <label className="field-label">Segredo do Webhook</label>
          <input
            type="password"
            className="field-input"
            value={values.api_webhook_secret ?? ''}
            onChange={(e) => setVal('api_webhook_secret', e.target.value)}
          />
        </div>
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        Eventos de documentos fiscais serao enviados via POST para a URL configurada.
      </p>
    </section>
  );
}
